import React from "react";
import ModalContentBaseXl from '../redux/components/modal/modal_content_base_xl';
import ModalAlert from '../redux/components/modal/modal_alert';
import ModalLoader from '../redux/components/modal/modal_loader';
import axios from '../axios';
import usePresignedImageUpload from '../../lib/use_presigned_image_upload';
import "babel-polyfill";
import { AiModalBody, AiField, aiInputStyle, AiPrimaryButton, AiResultHeader } from './ai_modal_parts';

export const GenerateOutlineFromPhotoModal = ({
  eventId,
  mainImageUrl,
  onAcceptEventTitle,
  onAcceptEventBody,
  onAcceptEventDate,
  onAcceptEventEndDatetime,
  onAcceptPlace,
  onMainImageConfirmed,
  onSubImageConfirmed,
  subImageCount,
}) => {
  const modalRef = React.useRef();
  const errorModalRef = React.useRef();
  const rateLimitModalRef = React.useRef();
  const loaderRef = React.useRef();
  const reflectLoaderRef = React.useRef();
  const photoInputRef = React.useRef();
  const { upload } = usePresignedImageUpload();

  const [photoCacheData, setPhotoCacheData] = React.useState('');
  const [photoPreviewUrl, setPhotoPreviewUrl] = React.useState('');
  const [eventTitle, setEventTitle] = React.useState('');
  const [eventBody, setEventBody] = React.useState('');
  const [eventDate, setEventDate] = React.useState('');
  const [eventEndDatetime, setEventEndDatetime] = React.useState('');
  const [place, setPlace] = React.useState('');
  const [isImageRegistered, setIsImageRegistered] = React.useState(true);
  // @context TGW-2195 読み取り前から空の「イベント名」等の欄が並ぶと「ここに入力して」と読まれるため、
  //   読み取りが終わるまで結果欄を出さない。
  const [hasResult, setHasResult] = React.useState(false);

  // イベント画像はメイン1枚＋サブ4枚が上限
  const isSubImageFull = !!mainImageUrl && subImageCount >= 4;

  // createObjectURL で作ったURLは明示的に解放しないとブラウザが画像を保持し続ける
  const revokePhotoPreviewUrl = () => {
    photoPreviewUrl && URL.revokeObjectURL(photoPreviewUrl);
  };

  const close = () => {
    revokePhotoPreviewUrl();
    setPhotoCacheData('');
    setPhotoPreviewUrl('');
    setEventTitle('');
    setEventBody('');
    setEventDate('');
    setEventEndDatetime('');
    setPlace('');
    setIsImageRegistered(true);
    setHasResult(false);
    modalRef.current && modalRef.current.closeModal();
  };

  const generateFromPhoto = async () => {
    const photoFile = photoInputRef.current.files[0];
    if (!photoFile) {
      return;
    }

    try {
      loaderRef.current.openModal();

      const uploadedCacheData = await upload(photoFile);
      setPhotoCacheData(uploadedCacheData);
      revokePhotoPreviewUrl();
      setPhotoPreviewUrl(URL.createObjectURL(photoFile));

      const response = await fetch('/api/event/generate_outline_from_image', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cache_data: uploadedCacheData, event_id: eventId }),
      });
      if (!response.ok) {
        loaderRef.current.closeModal();
        if (response.status === 429) {
          rateLimitModalRef.current.openModal();
        } else {
          errorModalRef.current.openModal();
        }
        return;
      }

      const json = await response.json();
      setHasResult(true);
      setEventTitle(json.result.title || '');
      setEventBody(json.result.body || '');
      setEventDate(json.result.event_date || '');
      setEventEndDatetime(json.result.event_end_datetime || '');
      setPlace(json.result.place || '');

      loaderRef.current.closeModal();
    } catch (err) {
      loaderRef.current.closeModal();
      errorModalRef.current.openModal();
    } finally {
      photoInputRef.current.value = '';
    }
  };

  const reflectToEventPage = async () => {
    if (eventTitle) {
      onAcceptEventTitle && onAcceptEventTitle(eventTitle);
    }
    if (eventBody) {
      onAcceptEventBody && onAcceptEventBody(eventBody);
    }
    if (eventDate) {
      onAcceptEventDate && onAcceptEventDate(eventDate);
    }
    if (eventEndDatetime) {
      onAcceptEventEndDatetime && onAcceptEventEndDatetime(eventEndDatetime);
    }
    if (place) {
      onAcceptPlace && onAcceptPlace(place);
    }

    // メイン画像が空なら1枚目に、埋まっていれば既存を上書きせず末尾に足す
    if (isImageRegistered && photoCacheData && !isSubImageFull) {
      try {
        reflectLoaderRef.current.openModal();
        if (mainImageUrl) {
          const res = await axios.post('/api/event_edit/confirm_sub_image_upload', {
            event_id: eventId,
            cache_data: photoCacheData,
          });
          onSubImageConfirmed && onSubImageConfirmed(res.data);
        } else {
          const res = await axios.post('/api/event/confirm_main_image_upload', {
            event_id: eventId,
            cache_data: photoCacheData,
          });
          onMainImageConfirmed && onMainImageConfirmed(res.data.main_image_url);
        }
        reflectLoaderRef.current.closeModal();
      } catch (error) {
        reflectLoaderRef.current.closeModal();
        alert(
          error?.response?.data?.error || 'イベント画像の登録に失敗しました。'
        );
        return;
      }
    }

    alert('イベントに反映しました。');
    close();
  };

  const previewContent = (
    <button
      type="button"
      className="a-button a-button--sm a-button--orange"
      style={{borderRadius: 8}}
    >
      写真から読み取る
    </button>
  );

  const modalContent = (
    <div>
      <div
        className="bg-white w-100 border-bottom"
        style={{
          zIndex: 9998,
          borderRadius: '16px 16px 0 0',
          position: 'fixed',
        }}
      >
        <button
          type="button"
          className="close close__modal position--modalclose"
          aria-label="Close"
          onClick={() => close()}
        >
            <span className="text-fz-24px float-right m-2" aria-hidden="true">
              ×
            </span>
        </button>
      </div>
      <AiModalBody
        title="写真から読み取る"
        description="チラシやメモの写真を選ぶと、AIがイベント名・紹介文・日時・場所を読み取ります。読み取った内容は反映する前に直せます。"
      >
        <input
          type="file"
          accept="image/*"
          id="generate-outline-photo"
          ref={photoInputRef}
          className="d-none"
          onChange={async () => await generateFromPhoto()}
        />
        {photoPreviewUrl &&
          <img
            src={photoPreviewUrl}
            alt="選択した写真"
            style={{
              width: '100%',
              maxHeight: 220,
              borderRadius: 12,
              objectFit: 'contain',
              background: '#F6F6F6',
              display: 'block',
              marginBottom: 16,
            }}
          />
        }
        <AiPrimaryButton onClick={() => photoInputRef.current.click()}>{hasResult ? '別の写真を選ぶ' : '写真を選ぶ'}</AiPrimaryButton>
        {hasResult &&
          <React.Fragment>
            <AiResultHeader title="読み取った内容" note="違っているところは、ここで直してから反映してください" />
            <AiField label="イベント名" htmlFor="generated-outline-photo-title">
              <textarea
                id="generated-outline-photo-title"
                className="tg-ai-input"
                style={{ ...aiInputStyle, height: 'auto', resize: 'vertical' }}
                rows={2}
                value={eventTitle}
                onChange={(e) => {
                  setEventTitle(e.target.value)
                }}
              />
            </AiField>
            <AiField label="紹介文" htmlFor="generated-outline-photo-body">
              <textarea
                id="generated-outline-photo-body"
                className="tg-ai-input"
                style={{ ...aiInputStyle, height: 200, overflow: 'auto', whiteSpace: 'pre-wrap', resize: 'vertical' }}
                value={eventBody}
                onChange={(e) => {
                  setEventBody(e.target.value)
                }}
              />
            </AiField>
            <div className="tg-ai-row-2col">
              <AiField label="開始日時" htmlFor="generated-outline-photo-event-date">
                <input
                  type="datetime-local"
                  id="generated-outline-photo-event-date"
                  className="tg-ai-input"
                  style={aiInputStyle}
                  value={eventDate}
                  onChange={(e) => {
                    setEventDate(e.target.value)
                  }}
                />
              </AiField>
              <AiField label="終了日時" htmlFor="generated-outline-photo-event-end-datetime">
                <input
                  type="datetime-local"
                  id="generated-outline-photo-event-end-datetime"
                  className="tg-ai-input"
                  style={aiInputStyle}
                  value={eventEndDatetime}
                  onChange={(e) => {
                    setEventEndDatetime(e.target.value)
                  }}
                />
              </AiField>
            </div>
            <AiField label="場所" htmlFor="generated-outline-photo-place">
              <input
                type="text"
                id="generated-outline-photo-place"
                className="tg-ai-input"
                style={aiInputStyle}
                value={place}
                placeholder="例）新宿駅西口"
                onChange={(e) => {
                  setPlace(e.target.value)
                }}
              />
            </AiField>
            <div style={{ marginBottom: 16 }}>
              {isSubImageFull
                ? <p style={{ fontSize: 12, color: '#8B9097', margin: 0 }}>イベント画像が上限の5枚に達しているため登録できません。</p>
                : <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#333', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={isImageRegistered}
                      onChange={(e) => setIsImageRegistered(e.target.checked)}
                    />
                    この写真をイベント画像に登録する
                  </label>
              }
            </div>
            <AiPrimaryButton onClick={async () => await reflectToEventPage()}>この内容を反映する</AiPrimaryButton>
          </React.Fragment>
        }
      </AiModalBody>
    </div>
  );
  return (
    <React.Fragment>
      <ModalContentBaseXl ref={modalRef} previewContent={previewContent} modalContent={modalContent} />
      <ModalAlert
        ref={errorModalRef}
        id="modal-generated-from-photo-error"
        title="自動生成失敗"
        content="自動生成に失敗しました。時間をおいてもう一度お試しください。"
      />
      <ModalAlert
        ref={rateLimitModalRef}
        id="modal-rate-limit-from-photo-error"
        title="自動生成失敗"
        content="1日の利用数を超過しました。24時間後にもう一度お試しください。"
      />
      <ModalLoader ref={loaderRef} message="約1分お待ちください" dummyProgressShown />
      <ModalLoader ref={reflectLoaderRef} />
    </React.Fragment>
  );
};
